// Custom Tooltip for Creative Share Chart

"use client";

import { cn } from "@/lib/utils";
import { TooltipProps } from "@/components/ui/tremor-charts/BarChart";
import { CreativeReportAd, DataGraphResponse } from "../types";
import {
  getColorClassName,
  getColorHex,
  type AvailableChartColorsKeys,
} from "@/lib/chartUtils";
import { formatGraphDateLabel } from "@/utils/dateUtils";
import { useGlobalStore } from "@/GlobalStore";
import MediaIcon from "@/components/common/MediaIcon";

import { Creative } from "../types";
import { RiMultiImageLine } from "@remixicon/react";
import {
  AGGREGATED_OTHERS_KEY,
  AGGREGATED_OTHERS_DISPLAY_NAME,
} from "./CreativeShareChart.constants";

interface CreativeShareTooltipProps extends TooltipProps {
  valueFormatter: (value: number) => string;
  graphData: DataGraphResponse | null;
  selectedMetric: string | null;
  processedTotalData: { [key: string]: string | number }[];
  primaryKeyToCreativeMap?: Record<string, CreativeReportAd>;
  chartColors?: AvailableChartColorsKeys[];
  allKeys?: string[];
  selectedInterval?: "daily" | "weekly" | "monthly";
}

export const CreativeShareTooltip = ({
  active,
  payload,
  label,
  valueFormatter,
  graphData,
  selectedMetric,
  processedTotalData,
  primaryKeyToCreativeMap,
  chartColors = [],
  allKeys = [],
  selectedInterval,
}: CreativeShareTooltipProps) => {
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);

  if (
    !active ||
    !payload ||
    !payload.length ||
    !graphData?.data?.data_totals ||
    !selectedMetric
  ) {
    return null;
  }
  // Find the processed total data point by matching the formatted label
  const processedTotalPoint = processedTotalData.find((dp) => dp.x === payload[0]?.index);

  // Get the total value from processedTotalPoint (already calculated)
  const total = Number(processedTotalPoint?.total || 0);
  const totalValue = Number(processedTotalPoint?.total_value || 0);
  // Get the original x value for lookup in raw data_totals
  const originalX = processedTotalPoint?.originalX || label;

  // Get date range from interval_ranges
  const intervalRange = graphData.data.interval_ranges?.find(
    (range) => range.label === originalX,
  );
  const dateRangeLabel = intervalRange
    ? formatGraphDateLabel(
        intervalRange.start_date,
        intervalRange.end_date,
        selectedInterval,
        selectedAccount?.time_zone,
      )
    : label;

  // Get the current item (first payload item, or the one being hovered)
  const currentItem = payload.find((item) => {
    const payloadItem = item as { type?: string };
    return payloadItem.type !== "none";
  });

  if (!currentItem) return null;

  const OTHERS_TEXT_COLOR = "#9AA3BD";

  const {
    value: currentValue,
    category: currentCategory,
    color: currentColor,
  } = currentItem;
  const currentPercentage = total > 0 ? (currentValue / total) * 100 : 0;
  const currentNumericValue = (currentPercentage * totalValue) / 100;

  // Check if current item is aggregated "Others" (not a real creative named "Others")
  const isCurrentAggregatedOthers = currentCategory === AGGREGATED_OTHERS_KEY;

  // Get current creative
  const currentCreative = primaryKeyToCreativeMap?.[currentCategory]?.creative;
  const currentCreativeImageUrl =
    currentCreative?.thumbnail_url ||
    currentCreative?.images?.[0]?.permalink_url ||
    currentCreative?.videos?.[0]?.thumbnail_url;

  // Determine creative type from images and videos
  const getCreativeType = (
    creative: Creative | undefined,
  ): "image" | "video" | "mixed" | null => {
    if (!creative) return null;
    const hasImages = creative.images && creative.images.length > 0;
    const hasVideos = creative.videos && creative.videos.length > 0;
    if (hasImages && hasVideos) return "mixed";
    if (hasVideos) return "video";
    if (hasImages) return "image";
    return null;
  };
  const currentCreativeType = getCreativeType(currentCreative);

  // Create a map of creative name to color
  const creativeColorMap = new Map<string, AvailableChartColorsKeys>();
  allKeys.forEach((creativeName, index) => {
    if (chartColors[index]) {
      creativeColorMap.set(creativeName, chartColors[index]);
    }
  });

  // Get all creatives from payload data (payload[0].payload contains all creative values)
  const payloadData = payload[0]?.payload as Record<string, unknown> | undefined;
  const allCreatives: Array<{
    name: string;
    value: number;
    percentage: number;
    color: AvailableChartColorsKeys | undefined;
  }> = [];

  if (payloadData && typeof payloadData === "object" && payloadData !== null) {
    Object.keys(payloadData).forEach((key) => {
      // Skip 'x' key as it's the date label
      if (key !== "x") {
        const value = payloadData[key];
        // Check if it's a number (creative value)
        if (typeof value === "number" && !isNaN(value) && value > 0) {
          allCreatives.push({
            name: key,
            value: value,
            percentage: total > 0 ? (value / total) * 100 : 0,
            color: creativeColorMap.get(key),
          });
        }
      }
    });
  }

  // Sort by value descending, filter out aggregated Others, and take top 10
  const topCreatives = allCreatives
    .filter((creative) => creative.name !== AGGREGATED_OTHERS_KEY)
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  // Get text color class from the payload color
  // Use hardcoded color for aggregated "Others" text
  const isHexOrRgba =
    typeof currentColor === "string" &&
    (currentColor.startsWith("#") || currentColor.startsWith("rgba"));
  const currentTextColorClass = isCurrentAggregatedOthers
    ? undefined
    : isHexOrRgba
      ? undefined
      : typeof currentColor === "string"
        ? getColorClassName(currentColor, "text")
        : undefined;
  const currentTextColorStyle = isCurrentAggregatedOthers
    ? { color: OTHERS_TEXT_COLOR }
    : isHexOrRgba
      ? { color: currentColor }
      : typeof currentColor === "string"
        ? { color: getColorHex(currentColor) }
        : undefined;

  return (
    <div
      className={cn(
        // base
        "rounded-md border text-sm shadow-md",
        // border color
        "border-gray-200 dark:border-gray-800",
        // background color
        "bg-white dark:bg-gray-950",
        "max-w-[350px]",
        "pointer-events-none overscroll-contain",
      )}
    >
      {/* Top Creative Summary Section */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          {/* Large Creative Image */}
          <div className="relative flex-shrink-0 w-[150px] h-[150px] rounded-[6px] overflow-hidden">
            {currentCreativeImageUrl ? (
              <img
                src={currentCreativeImageUrl}
                alt={currentCategory}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#F0F1F5]">
                <RiMultiImageLine size={50} className="shrink-0 text-[#CCD1DE]" />
              </div>
            )}
            {/* Media Type Icon */}
            {currentCreativeType && (
              <div className="absolute inset-0 flex items-end justify-end p-[6px] opacity-100">
                <MediaIcon type={currentCreativeType} />
              </div>
            )}
          </div>

          {/* Creative Name and Metrics */}
          <div className="h-[150px] flex flex-col justify-between py-[24px] flex-1 min-w-0">
            <p
              className={cn(
                "text-[14px]",
                "font-semibold font-Inter",
                "mb-1",
                "line-clamp-2",
                currentTextColorClass,
              )}
              style={currentTextColorStyle}
            >
              {currentCategory === AGGREGATED_OTHERS_KEY
                ? AGGREGATED_OTHERS_DISPLAY_NAME
                : currentCategory}
            </p>
            <div>
              <p
                className={cn(
                  "text-[16px]",
                  "font-medium",
                  "text-[#374151] dark:text-gray-50",
                  "mb-0.5",
                  "leading-[20px]",
                  "font-['Helvetica Neue',_Helvetica,_Arial,_sans-serif]",
                )}
              >
                {currentNumericValue === 0 || isNaN(currentNumericValue)
                  ? "—"
                  : valueFormatter(currentNumericValue)}
              </p>
              <p
                className={cn(
                  "text-[12px]",
                  "font-medium",
                  "leading-[20px]",
                  "[color:rgba(55,65,81,0.40)]",
                )}
              >
                {currentPercentage.toFixed(1)}% Vol
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Date and Total Value Section */}
      <div className="flex justify-between items-start self-stretch px-4 py-2 border-b border-gray-100">
        <span className="text-[#374151] font-inter text-[14px] font-medium leading-[20px] not-italic">
          {dateRangeLabel}
        </span>
        <span className="text-[14px] font-medium not-italic leading-[20px] [color:rgba(55,65,81,0.40)]">
          {valueFormatter(totalValue)}
        </span>
      </div>

      {/* Top 10 List */}
      <div className="max-h-[600px] flex flex-col py-2 pr-4 overflow-y-auto">
        {topCreatives.map((creative, index) => {
          const creativeData = primaryKeyToCreativeMap?.[creative.name]?.creative;
          const creativeImageUrl =
            creativeData?.thumbnail_url ||
            creativeData?.images?.[0]?.permalink_url ||
            creativeData?.videos?.[0]?.thumbnail_url;

          const isCurrentItem = creative.name === currentCategory;
          const colorHex = creative.color ? getColorHex(creative.color) : "#6B728099";

          return (
            <div
              key={`creative-${index}`}
              className={cn("flex items-center gap-[11px] h-[46px]")}
            >
              {/* Color Bar */}
              <div
                className="flex-shrink-0 w-1 h-[40px] rounded-r-full"
                style={{ backgroundColor: colorHex }}
              />
              <div
                className={cn(
                  "flex-1 overflow-hidden flex items-center justify-between gap-2 px-[7px] py-[3px] rounded-[6px]",
                  isCurrentItem && "bg-selected-gray",
                )}
              >
                <div className="flex items-center gap-[9px]">
                  {/* Creative Thumbnail */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-[6px] bg-[#F6F6F6] overflow-hidden border border-gray-200">
                    {creativeImageUrl ? (
                      <img
                        src={creativeImageUrl}
                        alt={creative.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#EDEFF3]">
                        <div className="w-6 h-6 rounded-full bg-[#D1D5DB] flex items-center justify-center">
                          <span className="text-[#6B7280] text-[10px] font-medium">
                            {creative.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Value and Percentage */}
                  <div className="flex-shrink-0 min-w-[100px]">
                    <p
                      className={cn(
                        "text-[13px]",
                        "font-medium",
                        "text-[#374151] dark:text-gray-50",
                        "leading-[18px]",
                        "font-['Helvetica Neue',_Helvetica,_Arial,_sans-serif]",
                      )}
                    >
                      {creative.value === 0 || isNaN(creative.value)
                        ? "—"
                        : `${valueFormatter((totalValue * creative.percentage) / 100)} / ${creative.percentage.toFixed(1)}%`}
                    </p>
                  </div>
                </div>

                {/* Creative Name */}
                <div className="flex-1 text-right min-w-0 overflow-hidden">
                  <p
                    className={cn(
                      "text-[13px]",
                      "font-normal",
                      "text-[#374151] dark:text-gray-300",
                      "leading-[18px]",
                      "truncate",
                    )}
                  >
                    {creative.name}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
